import styled from '@emotion/styled';
import { Divider, Input, Table, Tag } from 'antd';
import { sortBy } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { BondEntity, BondOptionsType } from '../../../../entities/bond.entity';
import { BROWN, PRIMARY, YELLOW } from '../../../../shared/colors';

const Container = styled.div`
  display: flex;
  color: white;

  .ant-divider-horizontal {
    margin: 0px 0px 10px 0px;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  > .left-box {
    border: 1px solid grey;
    border-radius: 5px;
    flex: 1;
    margin-right: 5px;
    padding: 2px;
    font-size: 12px;

    max-height: calc(100vh - 250px);
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 0.5vw;
    }

    &::-webkit-scrollbar-thumb {
      background-image: linear-gradient(to bottom, #eceff1, #eceff1);
      border-radius: 10px;
    }
  }

  > .right-box {
    border: 1px solid grey;
    border-radius: 5px;
    flex: 4.5;
    padding: 5px;

    max-height: calc(100vh - 250px);
    overflow-x: auto;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 0.5vw;
      height: 1vh;
    }

    &::-webkit-scrollbar-thumb {
      background-image: linear-gradient(to bottom, #eceff1, #eceff1);
      border-radius: 10px;
    }

    > .details {
      display: flex;

      > div {
        flex: 1;
        margin: 5px;
      }
    }
  }

  .elements {
    border: 1px solid grey;
    padding: 2px 5px;
    max-height: 90px;
    border-radius: 5px;
    background-color: ${PRIMARY};

    overflow-x: hidden;
    overflow-y: auto;
    margin-bottom: 3px;

    > p {
      margin: 0;
      cursor: pointer;
      padding-left: 2px;

      &:hover {
        background-color: ${BROWN};
        border-radius: 5px;
      }
    }

    &::-webkit-scrollbar {
      width: 0.5vw;
    }

    &::-webkit-scrollbar-thumb {
      background-image: linear-gradient(to bottom, #eceff1, #eceff1);
      border-radius: 10px;
    }
  }

  .ant-table-column-sort {
    background-color: inherit;
  }
`;

export const SearchContent = ({
  mainScreen,
  bondsData,
  bondOptions,
  getBond,
}: {
  mainScreen: boolean;
  bondsData: BondEntity[];
  bondOptions?: BondOptionsType;
  getBond: (b: BondEntity, ope: '1' | '2') => void;
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [bonds, setBonds] = useState<BondEntity[]>(bondsData);
  const [bondsFilter, setBondsFilter] = useState<BondEntity[]>(bondsData);
  const [countries, setCountries] = useState<string[]>([]);
  const [dateMaturation, setDateMaturation] = useState<string[]>([]);
  const [coupon, setCoupon] = useState<number[]>([]);
  const [filters, setFilters] = useState<FilterType[]>([]);

  useEffect(() => {
    setCountries(sortBy(bondOptions?.pays) || []);
    setDateMaturation(sortBy(bondOptions?.dateMaturite) || []);
    setCoupon(sortBy(bondOptions?.coupon) || []);
  }, [bondOptions]);

  useEffect(() => {
    if (filters) {
      console.log(filters);

      let resultFilter: BondEntity[] = bonds;
      for (let fil of filters) {
        if (fil.key === 'pays' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.pays),
          );
        } else if (fil.key === 'instrument' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.instrument),
          );
        } else if (fil.key === 'dateEmission' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.dateEmission),
          );
        } else if (fil.key === 'dateMaturite' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.dateMaturite),
          );
        } else if (fil.key === 'tauxMarginal' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(Number(res.tauxMarginal).toString()),
          );
        } else if (fil.key === 'code' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            res.code.includes(fil.values[0]),
          );
        }
      }
      setBondsFilter(resultFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const addFilter = (key: string, value: string) => {
    let dataFilters: FilterType[] = [];
    if (filters) {
      if (filters.find((fil) => fil.key === key) === undefined) {
        setFilters([...filters, { key, values: [value] }]);
      } else {
        for (let fil of filters) {
          if (fil.key === key && !fil.values.includes(value)) {
            dataFilters.push({ key: fil.key, values: [...fil.values, value] });
          } else {
            dataFilters.push(fil);
          }
        }
        setFilters(dataFilters);
      }
    } else {
      setFilters([{ key, values: [value] }]);
    }
  };

  const removeFilter = (key: string, value: string) => {
    let dataFilters: FilterType[] = [];
    if (filters) {
      for (let fil of filters) {
        if (fil.key === key && fil.values.includes(value)) {
          let index = fil.values.findIndex((val) => val === value);
          let temp = fil.values;
          temp.splice(index, 1);
          dataFilters.push({ key: fil.key, values: temp });
        } else {
          dataFilters.push(fil);
        }
      }
      setFilters(dataFilters);
    }
  };

  const lookupCode = (e: { target: { value: string } }) => {
    if (filters.length === 0) {
      addFilter('code', e.target.value);
    } else {
      const newFilters = filters.filter((e) => e.key !== 'code');
      newFilters.push({ key: 'code', values: [e.target.value] });
      setFilters(newFilters);
    }
  };

  return (
    <Container>
      <div className='left-box'>
        <TitleLine>Chercher par Code</TitleLine>
        <div className='elements' style={{ maxHeight: 100 }}>
          <Input
            placeholder='Code du Bond'
            size='small'
            onChange={lookupCode}
          />
        </div>
        <TitleLine>Instrument</TitleLine>
        <div className='elements' style={{ maxHeight: 100 }}>
          <p onClick={() => addFilter('instrument', 'OTA')}>OTA</p>
          <p onClick={() => addFilter('instrument', 'BTA')}>BTA</p>
          <p onClick={() => {}}>Emprunt oblig.</p>
        </div>

        <TitleLine>Pays</TitleLine>
        <div className='elements' style={{ maxHeight: 120 }}>
          {countries.map((val, index) => (
            <p key={index} onClick={() => addFilter('pays', val)}>
              {val}{' '}
            </p>
          ))}
        </div>

        <TitleLine>Date de maturation</TitleLine>
        <div className='elements'>
          {dateMaturation.map((val, index) => (
            <p key={index} onClick={() => addFilter('dateMaturite', val)}>
              {moment(val).format('DD/MM/YYYY')}
            </p>
          ))}
        </div>

        <TitleLine>Coupon</TitleLine>
        <div className='elements'>
          {coupon.map((val, index) => (
            <p
              key={index}
              onClick={() => addFilter('tauxMarginal', val.toString())}
            >
              {val}%{' '}
            </p>
          ))}
        </div>
      </div>

      <div className='right-box'>
        <p style={{ fontFamily: 'Poppins-B', fontSize: 18, margin: 0 }}>
          Liste des titres disponible
        </p>
        <Divider style={{ backgroundColor: 'white' }} />

        {filters && (
          <div style={{ marginBottom: 10 }}>
            {filters.map((filt, ind) => {
              return filt.values.map((val) => (
                <Tag
                  key={val}
                  color={YELLOW}
                  closable
                  onClose={() => removeFilter(filt.key, val)}
                >
                  {val}{' '}
                </Tag>
              ));
            })}
          </div>
        )}

        <Table
          columns={COLUMNS}
          dataSource={bondsFilter}
          rowSelection={{
            type: 'radio',
            onChange: (selectedRowKeys, selectedRows) => {
              getBond(selectedRows[0], '1');
            },
          }}
          rowClassName={(row, index) =>
            index % 2 === 0 ? 'even-row' : 'odd-row'
          }
          rowKey='id'
          size='small'
          style={{ zoom: mainScreen ? 0.8 : 1 }}
          pagination={false}
        />
      </div>
    </Container>
  );
};

export const TitleLine = ({ children }: { children: string }) => {
  return (
    <div>
      <p style={{ fontFamily: 'Poppins-R', marginBottom: 0 }}>{children} </p>
      <Divider style={{ backgroundColor: 'white' }} />
    </div>
  );
};

const COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: BondEntity, b: BondEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: BondEntity, b: BondEntity) =>
      a.instrument.localeCompare(b.instrument),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: BondEntity, b: BondEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: BondEntity) => (
      <div
        style={{
          width: 120,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.code}
      </div>
    ),
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    sorter: (a: BondEntity, b: BondEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Durée',
    dataIndex: 'dureeMaturite',
    sorter: (a: BondEntity, b: BondEntity) =>
      a.dureeMaturite.localeCompare(b.dureeMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: BondEntity, b: BondEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: BondEntity) => <>{Number(row.tauxMarginal)}</>,
  },
];

type FilterType = {
  key: string;
  values: string[];
};
